export function printDailySheet(date, plans, lines, projects, components, periodMdp, hitachiData) {
  const dateStr = new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const byLine = {};
  lines.forEach(l => { byLine[l.id] = { line: l, projects: {} }; });
  plans.forEach(p => {
    if (!byLine[p.line_id]) return;
    if (p.status === 'rescheduled') return;
    const pid = p.project_id;
    if (!byLine[p.line_id].projects[pid]) {
      const proj = projects.find(pr => pr.id === pid);
      byLine[p.line_id].projects[pid] = { project: proj || { name: p.project_name, category: p.category_name }, plans: [] };
    }
    byLine[p.line_id].projects[pid].plans.push(p);
  });

  let html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Daily Production Plan - ${date}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #1e293b; padding: 12px; }
  @media print { body { padding: 0; } .page-break { page-break-before: always; } }
  .header { text-align: center; margin-bottom: 16px; border-bottom: 2px solid #1e293b; padding-bottom: 8px; }
  .header h1 { font-size: 18px; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; }
  .header .date { font-size: 14px; font-weight: 600; color: #475569; margin-top: 2px; }
  .line-section { margin-bottom: 20px; }
  .line-header { background: #1e293b; color: white; padding: 6px 12px; font-size: 14px; font-weight: 700; border-radius: 4px 4px 0 0; display: flex; justify-content: space-between; align-items: center; }
  .line-header .badge { background: rgba(255,255,255,0.2); padding: 2px 8px; border-radius: 10px; font-size: 10px; }
  .project-block { border: 1px solid #e2e8f0; border-top: none; padding: 0; }
  .project-header { background: #f1f5f9; padding: 5px 12px; font-weight: 700; font-size: 12px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
  .project-header .cat { font-size: 9px; background: #dbeafe; color: #1d4ed8; padding: 1px 6px; border-radius: 8px; font-weight: 600; }
  .project-header .cat-set { background: #ffedd5; color: #c2410c; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #f8fafc; text-align: left; padding: 4px 8px; font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; border-bottom: 1px solid #e2e8f0; }
  th.right, td.right { text-align: right; }
  th.center, td.center { text-align: center; }
  td { padding: 4px 8px; border-bottom: 1px solid #f1f5f9; }
  tr:hover { background: #fafafa; }
  .seat-row { background: #eff6ff; }
  .group-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: #3b82f6; padding: 4px 8px; background: #f0f9ff; border-bottom: 1px solid #e2e8f0; }
  .group-label.other { color: #64748b; background: #f8fafc; }
  .set-comp-table { margin-left: 24px; margin-top: 4px; margin-bottom: 8px; }
  .set-comp-table td { font-size: 10px; padding: 2px 8px; }
  .weight { font-size: 9px; color: #94a3b8; }
  .bold { font-weight: 700; }
  .total-row { background: #f1f5f9; font-weight: 700; }
  .total-row td { border-top: 2px solid #cbd5e1; }
  .sign-area { margin-top: 24px; display: flex; justify-content: space-between; gap: 40px; }
  .sign-box { flex: 1; border-top: 1px solid #94a3b8; padding-top: 4px; text-align: center; font-size: 10px; color: #64748b; }
  .empty-msg { padding: 20px; text-align: center; color: #94a3b8; font-style: italic; }
  .footer { margin-top: 16px; text-align: center; font-size: 9px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 6px; }
</style></head><body>`;

  html += `<div class="header"><h1>Daily Production Plan</h1><div class="date">${dateStr}</div></div>`;

  let lineIdx = 0;
  for (const lineId of Object.keys(byLine)) {
    const { line, projects: lineProjects } = byLine[lineId];
    const projectEntries = Object.entries(lineProjects);
    if (projectEntries.length === 0) continue;

    if (lineIdx > 0) html += '<div class="page-break"></div>';

    const totalPlans = projectEntries.reduce((s, [, v]) => s + v.plans.length, 0);
    html += `<div class="line-section">`;
    html += `<div class="line-header"><span>${line.name} (${line.code})</span><span class="badge">${projectEntries.length} project(s) / ${totalPlans} items</span></div>`;

    for (const [pid, { project, plans: pPlans }] of projectEntries) {
      const isTrain = project?.planning_unit === 'seat' || pPlans[0]?.planning_unit === 'seat';
      const isSet = project?.planning_unit === 'set' || pPlans[0]?.planning_unit === 'set';
      const isHitachi = pPlans.some(p => p.is_hitachi);
      const catClass = isSet ? 'cat cat-set' : 'cat';
      const catLabel = project?.category || pPlans[0]?.category_name || '';
      const projComps = components[pid] || [];

      html += `<div class="project-block">`;
      html += `<div class="project-header"><span>${project?.name || pPlans[0]?.project_name} ${project?.sr_number ? '(' + project.sr_number + ')' : ''}</span><span class="${catClass}">${catLabel}</span></div>`;

      if (isHitachi && hitachiData) {
        // Hitachi: render each set's planned seat types
        const hd = hitachiData[pid] || {};
        const seatTypes = hd.seatTypes || [];

        pPlans.forEach(p => {
          const setNum = p.set_number;
          if (!setNum) return;
          const setData = hd.production?.[setNum] || {};
          const planned = setData.planned || {};
          const produced = setData.production || {};

          // Only show seat types that have planned quantities for this day
          const plannedTypes = seatTypes.filter(st => (planned[st.material_code] || 0) > 0);
          if (plannedTypes.length === 0) return;

          const persons = {};
          seatTypes.forEach(st => { persons[st.material_code] = st.persons || (st.name?.includes('2P') ? 2 : st.name?.includes('1P') ? 1 : 0); });

          html += `<table style="margin-bottom:12px">`;
          html += `<tr><td colspan="6" style="background:#1e40af;color:white;padding:4px 8px;font-weight:700;font-size:11px">Set #${setNum}</td></tr>`;
          html += `<tr><th style="width:35%">Seat Type</th><th class="center" style="width:12%">Req/Set</th><th class="center" style="width:12%">Planned</th><th class="center" style="width:14%">Seat Placed</th><th class="center" style="width:12%">Produced</th><th style="width:15%">Notes</th></tr>`;

          let totalPln = 0, totalSP = 0, totalPrd = 0;
          plannedTypes.forEach(st => {
            const pln = planned[st.material_code] || 0;
            const prd = produced[st.material_code] || 0;
            const sp = pln * (persons[st.material_code] || 0);
            const isSeat = st.type !== 'other';
            totalPln += pln; totalSP += isSeat ? sp : 0; totalPrd += prd;
            html += `<tr class="${isSeat ? 'seat-row' : ''}"><td>${isSeat ? '' : '<span style="font-size:8px;background:#e2e8f0;padding:1px 4px;border-radius:3px">OTH</span> '}${st.name}</td><td class="center">${st.qty_per_set}</td><td class="center bold">${pln}</td><td class="center">${isSeat ? sp : '-'}</td><td class="center">${prd}</td><td></td></tr>`;
          });
          html += `<tr class="total-row"><td>Subtotal Set #${setNum}</td><td></td><td class="center bold">${totalPln}</td><td class="center bold">${totalSP}</td><td class="center bold">${totalPrd}</td><td></td></tr>`;
          html += `</table>`;
        });
      } else if (isTrain) {
        const seatComps = projComps.filter(c => c.is_seat);
        const otherComps = projComps.filter(c => !c.is_seat && c.material_description);
        const planByMat = {};
        pPlans.forEach(p => { if (p.material_description) planByMat[p.material_description] = p; });
        const projMdp = periodMdp?.[pid]?.materials || {};
        // Build a secondary lookup by kiel_material_code for fallback matching
        const mdpByKielCode = {};
        Object.values(projMdp).forEach(m => {
          if (m.kiel_material_code) mdpByKielCode[m.kiel_material_code] = m;
        });

        // Only show components that are actually planned for this day
        const plannedSeatComps = seatComps.filter(c => {
          const plan = planByMat[c.material_description];
          return plan && (plan.planned_quantity > 0 || plan.completed_quantity > 0);
        });
        const plannedOtherComps = otherComps.filter(c => {
          const plan = planByMat[c.material_description];
          return plan && (plan.planned_quantity > 0 || plan.completed_quantity > 0);
        });

        html += `<table>`;
        html += `<tr><th style="width:28%">Component</th><th class="center" style="width:14%">Customer Code</th><th class="center" style="width:14%">Kiel Code</th><th class="center" style="width:10%">Planned</th><th class="center" style="width:12%">Seat Placed</th><th class="center" style="width:8%">Done</th><th style="width:14%">Notes</th></tr>`;

        if (plannedSeatComps.length > 0) {
          html += `<tr><td colspan="7" class="group-label">Seats (${plannedSeatComps.length} types)</td></tr>`;
          let totalPlanned = 0, totalSP = 0, totalDone = 0;
          plannedSeatComps.forEach(comp => {
            const plan = planByMat[comp.material_description];
            const mdpMat = projMdp[comp.material_description] || mdpByKielCode[comp.material_code] || {};
            const custCode = mdpMat.customer_material_code || '';
            const kielCode = mdpMat.kiel_material_code || comp.material_code || '';
            const pq = plan?.planned_quantity || 0;
            const spu = comp.seats_per_unit || 0;
            const sp = pq * spu;
            const done = plan?.completed_quantity || 0;
            totalPlanned += pq; totalSP += sp; totalDone += done;
            html += `<tr class="seat-row"><td>${comp.material_description}</td><td class="center" style="font-family:monospace;font-size:10px">${custCode}</td><td class="center" style="font-family:monospace;font-size:10px">${kielCode}</td><td class="center bold">${pq}</td><td class="center">${sp}</td><td class="center">${done}</td><td></td></tr>`;
          });
          html += `<tr class="total-row"><td>Subtotal Seats</td><td></td><td></td><td class="center bold">${totalPlanned}</td><td class="center bold">${totalSP}</td><td class="center bold">${totalDone}</td><td></td></tr>`;
        }

        if (plannedOtherComps.length > 0) {
          html += `<tr><td colspan="7" class="group-label other">Other Components (${plannedOtherComps.length})</td></tr>`;
          let totalPlanned = 0, totalDone = 0;
          plannedOtherComps.forEach(comp => {
            const plan = planByMat[comp.material_description];
            const mdpMat = projMdp[comp.material_description] || mdpByKielCode[comp.material_code] || {};
            const custCode = mdpMat.customer_material_code || '';
            const kielCode = mdpMat.kiel_material_code || comp.material_code || '';
            const pq = plan?.planned_quantity || 0;
            const done = plan?.completed_quantity || 0;
            totalPlanned += pq; totalDone += done;
            html += `<tr><td>${comp.material_description}</td><td class="center" style="font-family:monospace;font-size:10px">${custCode}</td><td class="center" style="font-family:monospace;font-size:10px">${kielCode}</td><td class="center bold">${pq}</td><td class="center">-</td><td class="center">${done}</td><td></td></tr>`;
          });
          html += `<tr class="total-row"><td>Subtotal Components</td><td></td><td></td><td class="center bold">${totalPlanned}</td><td></td><td class="center bold">${totalDone}</td><td></td></tr>`;
        }
        html += `</table>`;
      }

      if (isSet) {
        const setComps = project?.set_components || [];
        html += `<table>`;
        html += `<tr><th style="width:10%">Set #</th><th style="width:20%">Sets</th><th class="right" style="width:15%">Planned</th><th class="right" style="width:15%">Seats</th><th class="right" style="width:15%">Progress</th><th style="width:25%">Notes</th></tr>`;
        pPlans.forEach(p => {
          const ups = project?.units_per_set || p.units_per_set || 1;
          const cs = p.component_status || {};
          const cp = p.component_progress || {};
          // For tracked sets, derive progress from component_status
          let pct = p.completion_percentage || 0;
          if (p.track_components && cs.std_seat) {
            const comps = ['std_seat', 'flip_up', 'barrier'];
            const activeComps = comps.filter(ck => !cs[ck]?.carried_over && !cs[ck]?.rescheduled_to);
            const doneCount = activeComps.filter(ck => cs[ck]?.done).length;
            pct = activeComps.length > 0 ? Math.round((doneCount / activeComps.length) * 100) : 100;
          }
          const setLabel = p.set_number ? `Set ${p.set_number}` : '-';
          html += `<tr><td class="bold center">${setLabel}</td><td class="bold">${project?.name || p.project_name}</td><td class="right bold">${p.planned_quantity}</td><td class="right">${p.planned_quantity * ups}</td><td class="right bold">${pct}%</td><td></td></tr>`;

          // For tracked sets, show component_status instead of set_components
          if (p.track_components && cs.std_seat) {
            const COMP_MAP = { std_seat: 'Std Seat (70%)', flip_up: 'Flip-Up Seats (10%)', barrier: 'Barriers (20%)' };
            ['std_seat', 'flip_up', 'barrier'].forEach(ck => {
              const c = cs[ck] || {};
              if (c.carried_over || c.rescheduled_to) return; // skip carried/rescheduled
              const done = c.done ? 1 : 0;
              const shipped = c.shipped ? 'Shipped' : '';
              html += `<tr style="background:#fefce8"><td></td><td style="padding-left:24px">${COMP_MAP[ck]}</td><td class="right">1</td><td></td><td class="right">${done}/1 (${done * 100}%) ${shipped}</td><td></td></tr>`;
            });
          } else if (setComps.length > 0 && p.planned_quantity > 0) {
            setComps.forEach(sc => {
              const done = cp[sc.name] || 0;
              const compPct = p.planned_quantity > 0 ? Math.round((done / p.planned_quantity) * 100) : 0;
              html += `<tr style="background:#fefce8"><td></td><td style="padding-left:24px">${sc.name} <span class="weight">(${sc.weight}%)</span></td><td class="right">${p.planned_quantity}</td><td></td><td class="right">${done}/${p.planned_quantity} (${compPct}%)</td><td></td></tr>`;
            });
          }
        });
        html += `</table>`;
      }

      html += `</div>`;
    }

    html += `<div class="sign-area">
      <div class="sign-box">Prepared by</div>
      <div class="sign-box">Team Leader - ${line.code}</div>
      <div class="sign-box">Production Manager</div>
    </div>`;
    html += `</div>`;
    lineIdx++;
  }

  if (lineIdx === 0) {
    html += `<div class="empty-msg">No production plans for ${dateStr}</div>`;
  }

  html += `<div class="footer">Generated on ${new Date().toLocaleString()} | Kiel Production Planning System</div>`;
  html += `</body></html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
  setTimeout(() => win.print(), 500);
}
